import {
  giftPassBundleCreateSchema,
  GIFT_PASS_BUNDLE_PERCENT_MAX,
  GIFT_PASS_BUNDLE_PERCENT_MIN,
  TicketSalesCampaignDiscountType,
  type GiftPassBundle,
  type GiftPassBundleCreateRequest,
  type GiftPassBundleDiscountType,
  type GiftPassBundlePatchRequest,
  type GiftPassBundlePreviewQuery,
  type GiftPassBundleTag,
  type GiftPassTagColor,
} from '@bonsai/shared';

import { discountValueToText, parseDiscountText } from '../../../utils/discount-amount-text';

export interface BundleTagDraft {
  /** Stable across edits so React keeps each row's input focused. */
  key: string;
  text: string;
  color: GiftPassTagColor;
}

export interface BundleFormValues {
  name: string;
  passCount: string;
  discountType: GiftPassBundleDiscountType;
  /** As typed: whole percent, or dollars and cents off the whole bundle. */
  amount: string;
  tags: BundleTagDraft[];
  isActive: boolean;
  isPromoted: boolean;
}

/**
 * Keyed by form field: `name`, `passCount`, `amount`, `tags` (the list as a
 * whole), `tags.<n>` (one tag), `isActive`, `isPromoted`, and `form` for a
 * problem with the request as a whole.
 */
export type BundleFormErrors = Record<string, string>;

const DEFAULT_PASS_COUNT = '2';
let nextTagKey = 0;

export function newTagDraft(text = '', color: GiftPassTagColor = 'gold'): BundleTagDraft {
  nextTagKey += 1;
  return { key: `tag-${nextTagKey}`, text, color };
}

export function blankBundleForm(): BundleFormValues {
  return {
    name: '',
    passCount: DEFAULT_PASS_COUNT,
    discountType: TicketSalesCampaignDiscountType.PERCENT,
    amount: '',
    tags: [],
    isActive: true,
    isPromoted: false,
  };
}

export function bundleToForm(bundle: GiftPassBundle): BundleFormValues {
  return {
    name: bundle.name,
    passCount: String(bundle.passCount),
    discountType: bundle.discountType,
    amount: discountValueToText(bundle.discountType, bundle.discountValue),
    tags: bundle.tags.map((tag) => newTagDraft(tag.text, tag.color)),
    isActive: bundle.isActive,
    isPromoted: bundle.isPromoted,
  };
}

const WHOLE_RE = /^\d+$/;

export function passCountOf(text: string): number {
  return WHOLE_RE.test(text.trim()) ? Number(text.trim()) : NaN;
}

/** The stored discount value — whole percent, or cents — or null when the amount does not parse. */
export const discountValueOf = (type: GiftPassBundleDiscountType, amount: string) =>
  parseDiscountText(type, amount);

const tagsOf = (values: BundleFormValues): GiftPassBundleTag[] =>
  values.tags.map(({ text, color }) => ({ text: text.trim(), color }));

export function toCreateRequest(values: BundleFormValues): GiftPassBundleCreateRequest {
  return {
    name: values.name.trim(),
    passCount: passCountOf(values.passCount),
    discountType: values.discountType,
    discountValue: discountValueOf(values.discountType, values.amount) ?? 0,
    tags: tagsOf(values),
    isPromoted: values.isPromoted,
  };
}

/**
 * Only the fields that differ from `base`, or null when nothing does. A changed
 * type or amount sends both, since the number means something else under each
 * type.
 */
export function toPatchRequest(
  base: BundleFormValues,
  values: BundleFormValues,
): GiftPassBundlePatchRequest | null {
  const before = toCreateRequest(base);
  const after = toCreateRequest(values);
  const patch: GiftPassBundlePatchRequest = {};
  if (after.name !== before.name) patch.name = after.name;
  if (after.passCount !== before.passCount) patch.passCount = after.passCount;
  if (after.discountType !== before.discountType || after.discountValue !== before.discountValue) {
    patch.discountType = after.discountType;
    patch.discountValue = after.discountValue;
  }
  if (JSON.stringify(after.tags) !== JSON.stringify(before.tags)) patch.tags = after.tags;
  if (values.isActive !== base.isActive) patch.isActive = values.isActive;
  if (values.isPromoted !== base.isPromoted) patch.isPromoted = values.isPromoted;
  return Object.keys(patch).length > 0 ? patch : null;
}

export const isBundleFormDirty = (base: BundleFormValues, values: BundleFormValues) =>
  toPatchRequest(base, values) !== null;

export function formFieldOf(path: string): string {
  if (path === 'discountType' || path === 'discountValue') return 'amount';
  const tag = /^tags\.(\d+)\./.exec(path);
  if (tag) return `tags.${tag[1]}`;
  return path === '_' ? 'form' : path;
}

export function serverErrorsToForm(fieldErrors: Record<string, string>): BundleFormErrors {
  const errors: BundleFormErrors = {};
  for (const [path, message] of Object.entries(fieldErrors)) {
    errors[formFieldOf(path)] ??= message;
  }
  return errors;
}

function amountProblem(type: GiftPassBundleDiscountType): string {
  return type === TicketSalesCampaignDiscountType.PERCENT
    ? `Enter a percentage from ${GIFT_PASS_BUNDLE_PERCENT_MIN} to ${GIFT_PASS_BUNDLE_PERCENT_MAX}.`
    : 'Enter an amount greater than $0.';
}

export function validateBundleForm(values: BundleFormValues): BundleFormErrors {
  const result = giftPassBundleCreateSchema.safeParse(toCreateRequest(values));
  const errors: BundleFormErrors = {};
  if (!result.success) {
    for (const issue of result.error.issues) {
      errors[formFieldOf(issue.path.join('.'))] ??= issue.message;
    }
  }
  if (discountValueOf(values.discountType, values.amount) === null) {
    errors.amount = amountProblem(values.discountType);
  }
  if (values.isPromoted && !values.isActive) {
    errors.isPromoted = 'Only an active bundle can be promoted.';
  }
  return errors;
}

export function previewTerms(values: BundleFormValues): GiftPassBundlePreviewQuery | null {
  const errors = validateBundleForm(values);
  if (errors.passCount || errors.amount) return null;
  const body = toCreateRequest(values);
  return {
    passCount: body.passCount,
    discountType: body.discountType,
    discountValue: body.discountValue,
  };
}
